import { useEffect } from "react";

const SITE = "https://lifeisaproject.global";
const MARKER = "data-liap-content-hreflang";

/**
 * useContentHreflang — content-level hreflang for CMS detail pages.
 *
 * - Item WITH linked localized versions: alternates point at each
 *   published version's own URL (+ x-default → the EN version if one
 *   exists, else the item itself).
 * - Item with the legacy embedded translation slot (migrated EN/NL
 *   essays): leaves the generic ?lang= alternates untouched — the same
 *   URL serves both languages there.
 * - Single-language item: emits self + x-default only.
 *
 * Canonical remains self-referencing (managed by useSeo), so localized
 * versions never create duplicate-content ambiguity.
 */
export default function useContentHreflang(item, basePath) {
    const versions = item ? item.localized_versions : null;
    const hasSlot = !!(item && item.translation);

    useEffect(() => {
        if (!item) return undefined;
        if (hasSlot && !(versions && versions.length)) return undefined; // legacy ?lang= model

        // Replace the generic ?lang= alternates with content-level ones.
        document.head.querySelectorAll('link[data-liap-hreflang][rel="alternate"]').forEach((n) => n.remove());
        document.head.querySelectorAll(`link[${MARKER}]`).forEach((n) => n.remove());

        const self = { language: item.language || "en", slug: item.slug };
        const all = [self, ...(versions || [])];
        const seen = new Set();
        const add = (hreflang, slug) => {
            const el = document.createElement("link");
            el.setAttribute("rel", "alternate");
            el.setAttribute("hreflang", hreflang);
            el.setAttribute("href", `${SITE}/${basePath}/${slug}`);
            el.setAttribute(MARKER, "1");
            document.head.appendChild(el);
        };
        all.forEach((v) => {
            if (seen.has(v.language)) return;
            seen.add(v.language);
            add(v.language, v.slug);
        });
        const xdef = all.find((v) => v.language === "en") || self;
        add("x-default", xdef.slug);

        return () => {
            document.head.querySelectorAll(`link[${MARKER}]`).forEach((n) => n.remove());
        };
    }, [item, basePath, versions, hasSlot]);
}
